// src/components/FamilyGrid.js

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThList, faTh, faFilePdf, faPlus, faSpinner } from '@fortawesome/free-solid-svg-icons'; // Added faSpinner
import React, { useState, useEffect, useMemo } from 'react'; // Keep useState, useEffect, useMemo
import { Link } from 'react-router-dom';
import { Alert, Spinner, Container } from 'react-bootstrap';

// Import Firebase services needed directly
import { db, storage, functions } from '../firebase'; // Import functions instance
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions'; // Import httpsCallable directly

// Import the Auth Context hook
import { useAuth } from '../context/AuthContext';

import './FamilyGrid.css';

// --- REMOVED: getOrgId function ---

// --- Callable Function Reference for PDF ---
const generateDirectoryPdfCallable = httpsCallable(functions, 'generateDirectoryPdf');

// --- FamilyGrid Component ---
function FamilyGrid() {
  // *** Use context values ***
  const { currentUser, isAdmin, userOrgId, loadingAuth } = useAuth();

  // Local state for component data and UI status
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true); // For fetching family data specifically
  const [error, setError] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false); // State for PDF generation
  const [pdfMessage, setPdfMessage] = useState({ type: '', text: '' }); // State for PDF messages

  // *** REMOVED: Local state for userOrgId, isAdmin ***
  // *** REMOVED: Effect 1 (Fetch orgId/Admin Status) ***

  // --- Effect 2: Fetch Families (depends on context) ---
  useEffect(() => {
     // *** Use userOrgId from context, wait for auth to load ***
     if (loadingAuth || !userOrgId) {
        // If auth done loading but orgId missing, show error
        if (!loadingAuth && currentUser && !userOrgId) {
            setError("Organization details missing from your account. Cannot load families.");
            console.error("FamilyGrid: OrgId missing from context after auth loaded.");
            setLoading(false);
        }
        // Stop loading indicator only if auth is done but we can't fetch
        if (!loadingAuth) {
            setLoading(false);
        }
        return; // Exit if auth loading or orgId missing
     }

    console.log("FamilyGrid: Auth loaded, OrgId found. Fetching families for orgId:", userOrgId);
    let isMounted = true;

    const fetchFamilies = async () => {
      setLoading(true); // Start loading family data
      setError(null);
      try {
        const familiesCollection = collection(db, 'family');
        const q = query(
            familiesCollection,
            where('orgId', '==', userOrgId),
            orderBy('surname')
        );
        const familySnapshot = await getDocs(q);

        if (!isMounted) return;

        const familyListPromises = familySnapshot.docs.map(async (doc) => {
          const familyData = { id: doc.id, ...doc.data() };
          let photoURL = '/assets/blank.png';
          if (familyData.photo) {
            try {
              if (familyData.photo.startsWith('http')) {
                  photoURL = familyData.photo;
              } else {
                  const photoRef = ref(storage, familyData.photo);
                  photoURL = await getDownloadURL(photoRef);
              }
            } catch (imageError) {
              if (imageError.code !== 'storage/object-not-found') {
                 console.error(`FamilyGrid: Error getting photo URL for ${familyData.photo}:`, imageError);
              }
              photoURL = '/assets/blank.png';
            }
          }
          return { ...familyData, photoURL };
        });

        const familyList = await Promise.all(familyListPromises);

        if (isMounted) {
            setFamilies(familyList);
        }

      } catch (err) {
        console.error('FamilyGrid: Error during fetchFamilies:', err);
        if (isMounted) {
            // (Error handling logic remains similar)
            if (err.code === 'permission-denied') { setError("Permission denied. Check Firestore security rules."); }
            else if (err.code === 'failed-precondition' && err.message.includes('index')) { setError("Database index missing. Check console."); console.error("Firestore Index Required:", err); }
            else { setError(err.message || "An error occurred while fetching families."); }
        }
      } finally {
        if (isMounted) {
            setLoading(false); // Finish loading family data
        }
      }
    };

    fetchFamilies();

    return () => { isMounted = false; };
  // *** Update dependencies ***
  }, [userOrgId, loadingAuth, currentUser]);

  // --- Handler: Generate and Download PDF (copied from FamilyList) ---
  const handleGeneratePdf = async () => {
      console.log("Attempting to generate PDF directory...");
      setIsGeneratingPdf(true); setPdfMessage({ type: '', text: '' }); setError(null);
      try {
          const result = await generateDirectoryPdfCallable({});
          const data = result.data;
          if (data?.success && data.filePath) {
              setPdfMessage({ type: 'info', text: 'PDF generated. Preparing download...' });
              const bucketName = data.filePath.split('/')[2];
              const relativePath = data.filePath.substring(`gs://${bucketName}/`.length);
              const storageRef = ref(storage, relativePath);
              const downloadUrl = await getDownloadURL(storageRef);
              const link = document.createElement('a'); link.href = downloadUrl; link.download = 'directory.pdf';
              document.body.appendChild(link); link.click(); document.body.removeChild(link);
              setPdfMessage({ type: 'success', text: 'PDF download started!' }); setTimeout(() => setPdfMessage({ type: '', text: '' }), 5000);
          } else if (data?.success && !data.filePath) {
              setPdfMessage({ type: 'warning', text: 'Directory generation complete, but no families or members were found to include.' });
          } else {
              setPdfMessage({ type: 'danger', text: `Error: ${data?.message || "PDF generation failed on the server."}` });
          }
      } catch (err) { setPdfMessage({ type: 'danger', text: `Error: ${err.message || "Could not generate PDF."}` }); }
      finally { setIsGeneratingPdf(false); }
  };

   const groupFamiliesIntoRows = (families, itemsPerRow) => {
    const rows = [];
    for (let i = 0; i < families.length; i += itemsPerRow) {
      rows.push(families.slice(i, i + itemsPerRow));
    }
    return rows;
  };


  // --- Render Logic ---

  // Combined loading state
  if (loadingAuth || loading) {
    return (
        <div className="container text-center mt-5" aria-live="polite">
            <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
            </Spinner>
        </div>
    );
  }

  // Check user after auth load
  if (!currentUser) {
      return (
          <Container className="mt-3">
              <Alert variant="warning">Please <Alert.Link as={Link} to="/login">log in</Alert.Link> to view families.</Alert>
          </Container>
      );
  }

  // Check for fetch error
  if (error) {
    return <div className="alert alert-danger m-3">Error: {error}</div>;
  }

  const familyRows = groupFamiliesIntoRows(families, 3);

  console.log("FamilyGrid: Rendering Grid content, number of families:", families.length);
  return (
    <div className="col-md-12">
      <br />

      {/* Breadcrumb and View Toggle Row */}
      <div className="row mx-1 mb-3 align-items-center">
        {/* Breadcrumbs Column */}
        <div className="col">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0 bg-light px-2 py-1 rounded-1 small"> {/* Style breadcrumb */}
              <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
              <li className="breadcrumb-item"><Link to="/families">Families</Link></li>
              <li className="breadcrumb-item active" aria-current="page">Grid View</li>
            </ol>
          </nav>
        </div>
        {/* Action Buttons & View Toggle Column */}
        <div className="col-auto d-flex align-items-center flex-wrap"> {/* Added flex-wrap */}

            {/* --- MODIFIED: Admin Buttons using context isAdmin --- */}
            {isAdmin && (
                <Link to="/families/add" className="btn btn-sm btn-success me-2 mb-1 mb-md-0" title="Add New Family"> {/* Added mb spacing */}
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    <span className="d-none d-md-inline">Add Family</span>
                </Link>
            )}
            {isAdmin && (
                 <button
                    type="button"
                    className="btn btn-sm btn-secondary me-2 mb-1 mb-md-0" // Added mb spacing
                    onClick={handleGeneratePdf}
                    disabled={isGeneratingPdf || !userOrgId} // Use userOrgId from context
                    title={isGeneratingPdf ? "Generating PDF..." : "Download Directory PDF"}
                    aria-label={isGeneratingPdf ? "Generating directory PDF, please wait" : "Download directory PDF"}
                  >
                    {isGeneratingPdf ? ( <FontAwesomeIcon icon={faSpinner} spin className="me-1" /> ) : ( <FontAwesomeIcon icon={faFilePdf} className="me-1" /> )}
                    <span className="d-none d-md-inline"> {isGeneratingPdf ? "Generating..." : "Download PDF"} </span>
                  </button>
            )}
            {/* --- END MODIFIED --- */}

            {/* View Toggle */}
            <span className="text-muted me-2 d-none d-sm-inline small">View:</span>
            <div className="btn-group btn-group-sm mb-1 mb-md-0" role="group" aria-label="View toggle"> {/* Added mb spacing */}
                 <Link to="/families" className="btn btn-outline-secondary" title="List View"> <FontAwesomeIcon icon={faThList} aria-hidden="true" /> </Link>
                 <button type="button" className="btn btn-primary active" disabled aria-current="page" title="Grid View (Current)"> <FontAwesomeIcon icon={faTh} aria-hidden="true" /> </button>
            </div>
        </div>
      </div>

      {/* PDF Message Area */}
      {pdfMessage.text && (
          <Alert variant={pdfMessage.type || 'info'} className="mx-1" onClose={() => setPdfMessage({ type: '', text: '' })} dismissible>
              {pdfMessage.text}
          </Alert>
      )}

      {/* Family Grid Content */}
      {families.length === 0 ? (
        <Alert variant="info" className="m-3 text-center">
             No families found in this organization.
             {/* Use isAdmin from context */}
            {isAdmin && " Use the 'Add Family' button to create one."}
        </Alert>
      ) : (
        familyRows.map((row, rowIndex) => (
          <div className="row g-3 mb-3 px-2" key={`row-${rowIndex}`}>
            {row.map((family) => (
              <div className="col-md-4" key={family.id}>
                <Link to={`/families/${family.id}`} className="text-decoration-none">
                  <div className="card family-grid-item h-100">
                    <div className="card-img-container">
                        <img
                          src={family.photoURL}
                          className="card-img-top family-grid-photo"
                          alt={`${family.surname || 'Family'} photo`}
                          onError={(e) => { e.target.onerror = null; e.target.src='/assets/blank.png'; }}
                        />
                        <div className="overlay">
                          <div className="text">{family.surname || 'N/A'}</div>
                        </div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
            {/* Placeholder columns */}
            {row.length < 3 && Array(3 - row.length).fill(0).map((_, index) => ( <div className="col-md-4" key={`placeholder-${rowIndex}-${index}`}></div> )) }
          </div>
        ))
      )}
    </div>
  );
}

export default FamilyGrid;